import { apiClient } from '@/shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';
import type {
  MassagerOption,
  RezeptInitResponse,
  RezeptListItem,
  RezeptSetStatusParams,
  RezeptStartPdfParams,
  RezeptStartPdfResponse,
  RezeptPrintProgress,
} from '@/types/rezept.types';

interface ApiEnvelope<T> {
  success: boolean;
  message?: string;
  data: T;
}

/**
 * GET /api/v1/clinic/{site_type}/{clinic_number}/rezepts/init
 */
export async function getRezeptInit(
  tenant: string,
  clinicNumber: string
): Promise<RezeptInitResponse> {
  const res = await apiClient.get<ApiEnvelope<RezeptInitResponse>>(
    clinicApiPath(tenant, clinicNumber, '/rezepts/init')
  );
  return res.data.data;
}

/**
 * POST /api/v1/clinic/{site_type}/{clinic_number}/rezepts/search
 */
export async function getRezeptList(
  tenant: string,
  clinicNumber: string,
  params: Record<string, unknown>
): Promise<{
  rezepts: RezeptListItem[];
  print_num: number;
  is_attachement_document: boolean;
  is_visit_report_document: boolean;
}> {
  const res = await apiClient.post<
    ApiEnvelope<{
      rezepts: RezeptListItem[];
      print_num: number;
      is_attachement_document: boolean;
      is_visit_report_document: boolean;
    }>
  >(clinicApiPath(tenant, clinicNumber, '/rezepts/search'), params);
  return res.data.data;
}

/**
 * POST /api/v1/clinic/{site_type}/{clinic_number}/rezepts/{id}/add-treatment-report
 */
export async function saveAddTreatmentReport(
  tenant: string,
  clinicNumber: string,
  rezeptId: number,
  value: 0 | 1
): Promise<void> {
  await apiClient.post(
    clinicApiPath(tenant, clinicNumber, `/rezepts/${rezeptId}/add-treatment-report`),
    { add_treatment_report: value }
  );
}

export interface LongTermPatientItem {
  rezept_id: number;
  patient_id: number;
  serial_number: string;
  patient_name: string;
  first_treatment_date: string | null;
  treatement_yearmonth: number;
  visit_count: number;
  longterm_id: number | null;
}

/**
 * GET /api/v1/clinic/.../rezepts/long-term-patients
 */
export async function getLongTermPatients(
  tenant: string,
  clinicNumber: string,
  searchYearmonth: string,
  massagerId?: string
): Promise<LongTermPatientItem[]> {
  const params = new URLSearchParams({ search_yearmonth: searchYearmonth });
  if (massagerId) params.append('massager_id', massagerId);
  const res = await apiClient.get<ApiEnvelope<{ patients: LongTermPatientItem[] }>>(
    clinicApiPath(tenant, clinicNumber, `/rezepts/long-term-patients?${params}`)
  );
  return res.data.data.patients;
}

export interface KakuteiConfirmData {
  group_a_count: number;
  group_b_count: number;
  current_mm: string;
  next_mm: string;
  next_yearmonth: string;
  search_yearmonth_wareki: string;
}

/**
 * POST /api/v1/clinic/{site_type}/{clinic_number}/rezepts/kakutei-init
 */
export async function kakuteiInit(
  tenant: string,
  clinicNumber: string,
  searchYearmonth: string
): Promise<KakuteiConfirmData> {
  const res = await apiClient.post<ApiEnvelope<KakuteiConfirmData>>(
    clinicApiPath(tenant, clinicNumber, '/rezepts/kakutei-init'),
    { search_yearmonth: searchYearmonth }
  );
  return res.data.data;
}

/**
 * POST /api/v1/clinic/{site_type}/{clinic_number}/rezepts/kakutei
 */
export async function kakuteiExecute(
  tenant: string,
  clinicNumber: string,
  searchYearmonth: string
): Promise<void> {
  await apiClient.post(
    clinicApiPath(tenant, clinicNumber, '/rezepts/kakutei'),
    { search_yearmonth: searchYearmonth }
  );
}

/**
 * POST /api/v1/clinic/{site_type}/{clinic_number}/rezepts/reset-print
 */
export async function resetRezeptPrint(
  tenant: string,
  clinicNumber: string,
  rezeptIds: number[]
): Promise<void> {
  await apiClient.post(
    clinicApiPath(tenant, clinicNumber, '/rezepts/reset-print'),
    { rezept_ids: rezeptIds }
  );
}

/**
 * PUT /api/v1/clinic/{site_type}/{clinic_number}/rezepts/{id}/edit-status
 * Edit record's status modal (BD: Edit record's status — double-click row).
 *
 *   action=update_yearmonth → body needs `rezept_yearmonth` (YYYYMM string)
 *   action=revert_status     → 確定→印刷済→未印刷 (1 step per call)
 */
export type EditRezeptStatusPayload =
  | { action: 'update_yearmonth'; rezept_yearmonth: string }
  | { action: 'revert_status' };

export async function editRezeptStatus(
  tenant: string,
  clinicNumber: string,
  rezeptId: number,
  payload: EditRezeptStatusPayload,
): Promise<{ status: number; rezept_yearmonth: string }> {
  const res = await apiClient.put<
    ApiEnvelope<{ status: number; rezept_yearmonth: string }>
  >(
    clinicApiPath(tenant, clinicNumber, `/rezepts/${rezeptId}/edit-status`),
    payload,
  );
  return res.data.data;
}

/**
 * GET /rezepts/{id}/outcome — initial data for 転帰編集 modal (R2 only).
 */
export interface RezeptOutcomeData {
  id: number;
  treatement_yearmonth: number;
  patient_id: number;
  serial_number: string;
  patient_name: string | null;
  outcome_type_id: number | null;
  stop_term_from: string | null;
  stop_term_to: string | null;
  comment: string | null;
}

export async function getRezeptOutcome(
  tenant: string,
  clinicNumber: string,
  rezeptId: number,
): Promise<RezeptOutcomeData> {
  const res = await apiClient.get<ApiEnvelope<RezeptOutcomeData>>(
    clinicApiPath(tenant, clinicNumber, `/rezepts/${rezeptId}/outcome`),
  );
  return res.data.data;
}

export interface UpdateRezeptOutcomePayload {
  outcome_type_id: 1 | 2 | 3 | 4;
  stop_term_from?: string | null;
  stop_term_to?: string | null;
  comment?: string | null;
}

export async function updateRezeptOutcome(
  tenant: string,
  clinicNumber: string,
  rezeptId: number,
  payload: UpdateRezeptOutcomePayload,
): Promise<void> {
  await apiClient.put(
    clinicApiPath(tenant, clinicNumber, `/rezepts/${rezeptId}/outcome`),
    payload,
  );
}

/**
 * POST /api/v1/clinic/{site_type}/{clinic_number}/rezepts/search-massagers
 */
export async function getRezeptMassagers(
  tenant: string,
  clinicNumber: string,
  searchYearmonth: string
): Promise<MassagerOption[]> {
  const res = await apiClient.post<ApiEnvelope<{ massagers: MassagerOption[] }>>(
    clinicApiPath(tenant, clinicNumber, '/rezepts/search-massagers'),
    { search_yearmonth: searchYearmonth }
  );
  return res.data.data.massagers;
}

/**
 * POST /api/v1/clinic/{site_type}/{clinic_number}/rezepts/gaiji-list-pdf
 * Returns the 外字リスト (手書き修正対象者リスト) print page as an HTML string.
 */
export async function getGaijiListPdfHtml(
  tenant: string,
  clinicNumber: string,
  params: Record<string, unknown>
): Promise<string> {
  const res = await apiClient.post<string>(
    clinicApiPath(tenant, clinicNumber, '/rezepts/gaiji-list-pdf'),
    params,
    { responseType: 'text' }
  );
  return res.data;
}

/**
 * POST /api/v1/clinic/{site_type}/{clinic_number}/rezepts/set-status
 */
export async function setRezeptStatus(
  tenant: string,
  clinicNumber: string,
  params: RezeptSetStatusParams
): Promise<{ updated_count: number }> {
  const res = await apiClient.post<ApiEnvelope<{ updated_count: number }>>(
    clinicApiPath(tenant, clinicNumber, '/rezepts/set-status'),
    params
  );
  return res.data.data;
}

/**
 * POST /api/v1/clinic/{site_type}/{clinic_number}/rezepts/start-pdf
 */
export async function startPdf(
  tenant: string,
  clinicNumber: string,
  params: RezeptStartPdfParams
): Promise<RezeptStartPdfResponse> {
  const res = await apiClient.post<ApiEnvelope<RezeptStartPdfResponse>>(
    clinicApiPath(tenant, clinicNumber, '/rezepts/start-pdf'),
    params
  );
  return res.data.data;
}

/**
 * GET /api/v1/clinic/{site_type}/{clinic_number}/rezepts/print-progress/{token}
 */
export async function getPrintProgress(
  tenant: string,
  clinicNumber: string,
  token: string
): Promise<RezeptPrintProgress> {
  const res = await apiClient.get<ApiEnvelope<RezeptPrintProgress>>(
    clinicApiPath(tenant, clinicNumber, `/rezepts/print-progress/${token}`)
  );
  return res.data.data;
}

/**
 * GET /api/v1/clinic/{site_type}/{clinic_number}/rezepts/read-pdf/{token}
 */
export async function fetchPdfBlob(
  tenant: string,
  clinicNumber: string,
  token: string
): Promise<Blob> {
  const res = await apiClient.get<Blob>(
    clinicApiPath(tenant, clinicNumber, `/rezepts/read-pdf/${token}`),
    { responseType: 'blob' }
  );
  return res.data;
}
